import { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  CardTitle,
} from "reactstrap";
import EnterpriseService from "../../common/services/enterprise-service";
import Select from "react-select";
import reactSelectCustomStyles from "../../common/plugins/reactSelectStyles";
import Paginator from "../ui/Paginator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import truckyService from "../../common/services/trucky-service";
import CustomBadge from "../ui/CustomBadge";
import { Link } from "react-router-dom";
import RandomCompanies from "./RandomCompanies";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import LazyImage from "../../web/components/common/LazyImage";

const Directory = ({ container }) => {
  const es = new EnterpriseService();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [searchParameters, setSearchParameters] = useState(
    truckyService.loadSearchParameters("enterprise_directory", {
      name: "",
      country_code: "",
      company_type: "",
      language: "",
      recruitment: "",
    }),
  );
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, perPage: 18 });
  const [totalPages, setTotalPages] = useState(0);
  const [companyStats, setCompanyStats] = useState(null);

  useEffect(() => {
    truckyService.saveSearchParameters(
      "enterprise_directory",
      searchParameters,
    );
  }, [searchParameters]);

  useEffect(() => {
    getCompanies();
  }, [pagination]);

  useEffect(() => {
    truckyService.setDocumentTitle("VTC Directory");
    getCountries();
    getLanguages();
    es.getGlobalCompanyStats().then(setCompanyStats);
  }, []);

  const getCountries = async () => {
    setCountries(await es.getCountries());
  };

  const getLanguages = async () => {
    setLanguages(await es.getLanguages());
  };

  const getCompanies = async () => {
    setLoading(true);

    const result = await es.getCompanies(searchParameters, pagination);

    if (result != null) {
      setCompanies(result.data);
      setTotalPages(result.last_page);
    }

    setLoading(false);
  };

  const getCompanyStyleBadge = (company) => {
    if (company.company_type == "realistic")
      return (
        <CustomBadge size="small" color="primary">
          {container.localeManager.strings.realisticEconomy}{" "}
          <FontAwesomeIcon className="ms-1" icon="users"></FontAwesomeIcon>
          {company.members_count}
        </CustomBadge>
      );

    if (company.company_type == "miles")
      return (
        <CustomBadge size="small" color="primary">
          {container.localeManager.strings.enterpriseMiles}{" "}
          <FontAwesomeIcon className="ms-1" icon="users"></FontAwesomeIcon>
          {company.members_count}
        </CustomBadge>
      );

    if (company.company_type == "both")
      return (
        <CustomBadge size="small" color="primary">
          {container.localeManager.strings.ecoAndMiles}{" "}
          <FontAwesomeIcon className="ms-1" icon="users"></FontAwesomeIcon>
          {company.members_count}
        </CustomBadge>
      );

    return null;
  };

  const search = () => {
    setPagination({ ...pagination, page: 1 });
  };

  const getCompanyLogo = (company) => {
    return (
      <LazyImage
        src={company.avatar_url}
        className="directory-company-logo mb-3 rounded"
        placeholder="/assets/company-placeholder.png"
      />
    );
  };

  const handlePageClick = (event) => {
    setPagination({ ...pagination, page: event.selected + 1 });
  };

  const handleRowsPerPageChange = (perPage) => {
    setPagination({ ...pagination, page: 1, perPage });
  };

  return (
    <div>
      <div className="page-hero">
        <span className="page-hero-icon">
          <FontAwesomeIcon icon="database" className="mx-0"></FontAwesomeIcon>
        </span>
        <div>
          <h1 className="page-hero-title">
            {container.localeManager.strings.vtcDirectory}
          </h1>
        </div>
      </div>
      <LoadingOverlay
        active={loading}
        spinner={
          <Spinner
            color="primary"
            style={{ width: "4rem", height: "4rem" }}
          ></Spinner>
        }
      >
        <Row noGutters id="enterpriseContainer">
          <Col>
            <Card className="h-100">
              <CardBody>
                <>
                  <RandomCompanies
                    container={container}
                    cards={false}
                    carousel={true}
                  ></RandomCompanies>
                  <div className="filters-toolbar mb-3">
                    <FormGroup row>
                      <Col sm={6}>
                        <Label>
                          {container.localeManager.strings.companyStyle}
                        </Label>
                        <Input
                          value={searchParameters.company_type}
                          onChange={(e) =>
                            setSearchParameters({
                              ...searchParameters,
                              company_type: e.target.value,
                            })
                          }
                          type="select"
                        >
                          <option value="">
                            {container.localeManager.strings.all}
                          </option>
                          <option value="realistic">
                            {container.localeManager.strings.realisticEconomy}
                          </option>
                          <option value="miles">
                            {container.localeManager.strings.enterpriseMiles}
                          </option>
                          <option value="both">
                            {container.localeManager.strings.ecoAndMiles}
                          </option>
                        </Input>
                      </Col>
                      <Col sm={6}>
                        <Label>{container.localeManager.strings.name}</Label>
                        <Input
                          value={searchParameters.name}
                          type="text"
                          onChange={(e) =>
                            setSearchParameters({
                              ...searchParameters,
                              name: e.target.value,
                            })
                          }
                        ></Input>
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col sm={4}>
                        <Label>{container.localeManager.strings.country}</Label>
                        <Select
                          onChange={(val) =>
                            setSearchParameters({
                              ...searchParameters,
                              country_code: val == null ? "" : val.code,
                            })
                          }
                          value={countries.find(
                            (m) => m.code == searchParameters.country_code,
                          )}
                          options={countries}
                          styles={reactSelectCustomStyles}
                          getOptionLabel={(m) => m.name}
                          getOptionValue={(m) => m.code}
                          isClearable={true}
                          backspaceRemovesValue={true}
                        ></Select>
                      </Col>
                      <Col sm={4}>
                        <Label>
                          {container.localeManager.strings.languageWithoutDots}
                        </Label>
                        <Select
                          onChange={(val) =>
                            setSearchParameters({
                              ...searchParameters,
                              language: val == null ? "" : val.name,
                            })
                          }
                          value={languages.find(
                            (m) => m.name == searchParameters.language,
                          )}
                          options={languages}
                          styles={reactSelectCustomStyles}
                          getOptionValue={(m) => m.code}
                          getOptionLabel={(m) => m.name}
                          isClearable={true}
                          backspaceRemovesValue={true}
                        ></Select>
                      </Col>
                      <Col sm={2}>
                        <Label>
                          {container.localeManager.strings.recruitmentStatus}
                        </Label>
                        <Input
                          value={searchParameters.recruitment}
                          onChange={(e) =>
                            setSearchParameters({
                              ...searchParameters,
                              recruitment: e.target.value,
                            })
                          }
                          type="select"
                        >
                          <option value="">
                            {container.localeManager.strings.all}
                          </option>
                          <option value="open">
                            {container.localeManager.strings.open}
                          </option>
                          <option value="closed">
                            {container.localeManager.strings.closed}
                          </option>
                        </Input>
                      </Col>
                      <Col className="d-flex align-items-end justify-content-end mt-3 mt-md-none">
                        <Button size="sm" onClick={search} color="primary">
                          {container.localeManager.strings.search}
                        </Button>
                      </Col>
                    </FormGroup>
                  </div>
                  {!loading && (
                    <>
                      <div className="my-3">
                        <Paginator
                          rowsPerPageSelected={pagination.perPage}
                          rowsPerPage={[18, 36, 54, 72, 90]}
                          handleRowsPerPageChange={handleRowsPerPageChange}
                          showRowsPerPage={true}
                          forcePage={pagination.page - 1}
                          totalPages={totalPages}
                          handlePageClick={handlePageClick}
                        ></Paginator>
                      </div>
                      <Row className="directory-company-grid g-4">
                        {companies.map((m) => {
                          return (
                            <Col key={m.id} xs={12} md={6} xl={4}>
                              <Card className="directory-company-card h-100">
                                <CardBody className="text-center d-flex flex-column">
                                  <Link
                                    to={`/company/${m.id}`}
                                    className="directory-company-link d-flex flex-column h-100"
                                  >
                                    {getCompanyLogo(m)}
                                    <CardTitle className="text-uppercase font-weight-bold mb-1">
                                      <h4 className="company-name mb-0">{m.name}</h4>
                                    </CardTitle>
                                    {m.slogan && (
                                      <p className="company-slogan font-italic mt-1 mb-3">{m.slogan}</p>
                                    )}
                                    <div className="company-badge-row mt-auto">
                                      {m.flag_url != null && (
                                        <img src={m.flag_url} className="company-flag" alt="flag" />
                                      )}
                                      {getCompanyStyleBadge(m)}
                                    </div>
                                  </Link>
                                </CardBody>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                      <Paginator
                        rowsPerPageSelected={pagination.perPage}
                        rowsPerPage={[18, 36, 54, 72, 90]}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        forcePage={pagination.page - 1}
                        totalPages={totalPages}
                        handlePageClick={handlePageClick}
                      ></Paginator>
                    </>
                  )}
                  {!loading && companies.length == 0 && (
                    <div className="notice d-flex bg-light-danger rounded border-danger border border-dashed p-6 mt-5">
                      {container.localeManager.strings.noCompanies}
                    </div>
                  )}
                </>
              </CardBody>
            </Card>
          </Col>
          {!container.dataService.data.enablePremiumFeature() && (
            <aside className="ad-column-placeholder">
              {!window.overwolf && (
                <BoxDynamicAd container={container} className="my-3" />
              )}
            </aside>
          )}
        </Row>
      </LoadingOverlay>
    </div>
  );
};

export default Directory;
